import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthError, Session, User } from '@supabase/supabase-js';
import { COMMO_SHARED_SUPABASE_CLIENT } from '../commo-shared-data-access.config';
import { isLinkIdentityConflict } from '../utils/link-identity-conflict';
import { safeAppReturnUrl } from '../utils/safe-app-return-url';

/** Supabase `signInWithOAuth` / `linkIdentity` で使うプロバイダ。 */
export type OAuthProvider = 'google' | 'apple';

const PENDING_OAUTH_PROVIDER_KEY = 'cmo_pending_oauth_provider';

const OAUTH_ERROR_QUERY_KEYS = [
  'error',
  'error_code',
  'error_description',
  'error_uri',
] as const;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly supabase = inject(COMMO_SHARED_SUPABASE_CLIENT);
  private readonly router = inject(Router);

  private readonly _session = signal<Session | null>(null);
  private readonly _user = signal<User | null>(null);
  private readonly _error = signal<AuthError | null>(null);
  private readonly _loading = signal<boolean>(false);

  readonly session = this._session.asReadonly();
  readonly user = this._user.asReadonly();
  readonly error = this._error.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly isAuthenticated = computed(() => this._session() !== null);

  /** 匿名ログイン中かどうか（Supabase User の is_anonymous を参照）。 */
  readonly isAnonymous = computed(
    () =>
      (this._user() as unknown as { is_anonymous?: boolean })?.is_anonymous ===
      true,
  );

  private linkConflictRecoveryStarted = false;

  constructor() {
    this.supabase.auth.onAuthStateChange((_event, session) => {
      this._session.set(session);
      this._user.set(session?.user ?? null);
      this._error.set(null);
    });

    void this.initializeSession();
  }

  private async initializeSession(): Promise<void> {
    try {
      this._loading.set(true);

      // authGuard より先に URL の link 衝突を処理する（セッション無しだと login へ飛ばされ query が消える）。
      if (await this.maybeRecoverLinkIdentityConflictFromUrl()) {
        return;
      }

      const {
        data: { session },
        error,
      } = await this.supabase.auth.getSession();
      if (error) {
        this._error.set(error);
      } else {
        // getSession は localStorage のみ。DB リセット後の幽霊 JWT は getUser で落とす。
        await this.resolveValidatedSession(session);
      }
    } catch (error) {
      this._error.set(error as AuthError);
    } finally {
      if (!this.linkConflictRecoveryStarted) {
        this._loading.set(false);
      }
    }
  }

  /**
   * 現在 URL（query / hash）の OAuth エラー。無ければ null。
   * Profile の戻りトースト等で利用する。
   */
  readOAuthErrorFromLocation(): {
    code: string;
    description: string;
  } | null {
    if (typeof window === 'undefined') return null;
    const search = new URLSearchParams(window.location.search);
    const hashRaw = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const hash = new URLSearchParams(hashRaw);
    const code = search.get('error_code') ?? hash.get('error_code') ?? '';
    const description =
      search.get('error_description') ?? hash.get('error_description') ?? '';
    const error = search.get('error') ?? hash.get('error') ?? '';
    if (!code && !description && !error) return null;
    return { code, description: description || error };
  }

  /**
   * OAuth エラー query / hash を消す。
   * `history.replaceState` だけだと Angular の queryParamMap が残り、
   * identity ボタンが already-in-use のまま非表示になるため Router で同期する。
   */
  async stripOAuthParamsFromUrl(): Promise<void> {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of OAUTH_ERROR_QUERY_KEYS) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (/error(_code|_description|_uri)?=/i.test(url.hash)) {
      url.hash = '';
      changed = true;
    }
    if (!changed) return;

    const next = url.pathname + url.search + url.hash;
    try {
      await this.router.navigateByUrl(next, { replaceUrl: true });
    } catch {
      window.history.replaceState({}, '', next);
    }
  }

  /**
   * OAuth 戻り URL に already-in-use があるとき、匿名を捨てて同じプロバイダでログインへ進む。
   * @returns プロバイダへ遷移するリカバリを開始できたら true（失敗時は false）
   */
  private async beginSignInAfterLinkConflict(
    provider: OAuthProvider,
    redirectToPath = '/profile',
  ): Promise<boolean> {
    if (this.linkConflictRecoveryStarted) return true;
    this.linkConflictRecoveryStarted = true;
    await this.stripOAuthParamsFromUrl();
    return this.signOutAndSignInWithOAuth(provider, redirectToPath);
  }

  /**
   * @returns 衝突を検出して OAuth ログインへ進めたら true。
   * 失敗時は false（呼び出し側は通常の getSession へ進む）。
   */
  private async maybeRecoverLinkIdentityConflictFromUrl(): Promise<boolean> {
    const err = this.readOAuthErrorFromLocation();
    if (!err) return false;
    if (!isLinkIdentityConflict(err.code, err.description)) return false;
    const path = this.redirectPathAfterLinkConflict();
    const provider = this.takeRememberedOAuthProvider();
    return this.beginSignInAfterLinkConflict(provider, path);
  }

  /** OAuth 成功後の戻り先。`/auth/*` 上で拾った場合は returnUrl か `/profile`。 */
  private redirectPathAfterLinkConflict(): string {
    if (typeof window === 'undefined') return '/profile';
    const pathname = window.location.pathname || '/profile';
    if (!pathname.startsWith('/auth')) {
      return this.normalizeAppPath(pathname);
    }
    const returnUrl = new URLSearchParams(window.location.search).get(
      'returnUrl',
    );
    const safe = safeAppReturnUrl(returnUrl, '/profile');
    try {
      const u = new URL(safe, window.location.origin);
      return this.normalizeAppPath(u.pathname || '/profile');
    } catch {
      return '/profile';
    }
  }

  async signInWithOAuth(
    provider: OAuthProvider,
    redirectToPath?: string,
  ): Promise<void> {
    try {
      this._loading.set(true);
      this._error.set(null);
      this.rememberOAuthProvider(provider);

      const { error } = await this.supabase.auth.signInWithOAuth({
        provider,
        options: {
          // 招待リンク等で `/auth/login?returnUrl=…` にいるとき、OAuth 後も同じ URL に戻し returnUrl を失わない
          redirectTo: this.oauthRedirectUrl(redirectToPath),
        },
      });

      if (error) {
        this._error.set(error);
      }
    } catch (error) {
      this._error.set(error as AuthError);
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * 現在の匿名ユーザーに OAuth アイデンティティをリンクする（同一 user id のまま永続アカウント化）。
   * Supabase Dashboard > Auth で「Manual linking」を有効にする必要がある。
   * そのアカウントが別ユーザーに既に紐づいている場合は匿名を捨てて同じプロバイダで入り直す。
   */
  async linkOAuthIdentity(
    provider: OAuthProvider,
    redirectToPath = '/profile',
  ): Promise<void> {
    try {
      this._loading.set(true);
      this._error.set(null);
      this.rememberOAuthProvider(provider);

      const path = this.normalizeAppPath(redirectToPath);
      const { error } = await this.supabase.auth.linkIdentity({
        provider,
        options: {
          redirectTo: `${window.location.origin}${path}`,
        },
      });

      if (error) {
        this._error.set(error);
        if (isLinkIdentityConflict(error.code ?? '', error.message)) {
          await this.beginSignInAfterLinkConflict(provider, path);
          return;
        }
      }
    } catch (error) {
      this._error.set(error as AuthError);
    } finally {
      // 衝突リカバリ成功時は signOutAndSignInWithOAuth が loading を維持する
      if (!this.linkConflictRecoveryStarted) {
        this._loading.set(false);
      }
    }
  }

  /**
   * 匿名セッションを捨てて、既存の OAuth アカウントでログインする。
   * `linkIdentity` が already-in-use のときの自動切替用（ゲストデータは移行しない）。
   * @returns プロバイダへ遷移できたら true
   */
  private async signOutAndSignInWithOAuth(
    provider: OAuthProvider,
    redirectToPath = '/profile',
  ): Promise<boolean> {
    const path = this.normalizeAppPath(redirectToPath);
    try {
      this._loading.set(true);
      this._error.set(null);
      this.rememberOAuthProvider(provider);

      const { error: signOutError } = await this.supabase.auth.signOut();
      if (signOutError) {
        this._error.set(signOutError);
        this.linkConflictRecoveryStarted = false;
        return false;
      }
      this._session.set(null);
      this._user.set(null);

      const { error } = await this.supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}${path}`,
        },
      });
      if (error) {
        this._error.set(error);
        this.linkConflictRecoveryStarted = false;
        return false;
      }
      // 成功時はプロバイダへ遷移。loading は true のまま authGuard を止める。
      return true;
    } catch (error) {
      this._error.set(error as AuthError);
      this.linkConflictRecoveryStarted = false;
      return false;
    } finally {
      if (!this.linkConflictRecoveryStarted) {
        this._loading.set(false);
      }
    }
  }

  private rememberOAuthProvider(provider: OAuthProvider): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.setItem(PENDING_OAUTH_PROVIDER_KEY, provider);
    } catch {
      // private mode 等では無視
    }
  }

  /**
   * 直近に開始した OAuth プロバイダ（sessionStorage）。
   * 複数 identity ボタンが並ぶとき、他プロバイダの衝突に反応しないために使う。
   */
  peekRememberedOAuthProvider(): OAuthProvider | null {
    if (typeof sessionStorage === 'undefined') return null;
    try {
      const value = sessionStorage.getItem(PENDING_OAUTH_PROVIDER_KEY);
      if (value === 'apple' || value === 'google') return value;
    } catch {
      // ignore
    }
    return null;
  }

  /** URL 衝突リカバリ用。読んだら消す。未記録時は google。 */
  private takeRememberedOAuthProvider(): OAuthProvider {
    const value = this.peekRememberedOAuthProvider();
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.removeItem(PENDING_OAUTH_PROVIDER_KEY);
      } catch {
        // ignore
      }
    }
    return value ?? 'google';
  }

  private normalizeAppPath(redirectToPath: string): string {
    return redirectToPath.startsWith('/') && !redirectToPath.startsWith('//')
      ? redirectToPath
      : '/profile';
  }

  /** OAuth `redirectTo`。明示パスがあればそれを使い、なければ現在 URL（query 含む）。 */
  private oauthRedirectUrl(redirectToPath?: string): string {
    if (redirectToPath !== undefined) {
      return `${window.location.origin}${this.normalizeAppPath(redirectToPath)}`;
    }
    return `${window.location.origin}${window.location.pathname}${window.location.search}`;
  }

  async signInAnonymously(): Promise<void> {
    try {
      this._loading.set(true);
      this._error.set(null);

      const { error } = await this.supabase.auth.signInAnonymously();

      if (error) {
        this._error.set(error);
      }
    } catch (error) {
      this._error.set(error as AuthError);
    } finally {
      this._loading.set(false);
    }
  }

  async signOut(): Promise<void> {
    try {
      this._loading.set(true);
      this._error.set(null);

      const { error } = await this.supabase.auth.signOut();

      if (error) {
        this._error.set(error);
      } else {
        this._session.set(null);
        this._user.set(null);
      }
    } catch (error) {
      this._error.set(error as AuthError);
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * localStorage の session を auth サーバーで検証する。
   * 行が無い JWT（DB リセット後など）は signOut して null。
   */
  private async resolveValidatedSession(
    session: Session | null,
  ): Promise<Session | null> {
    if (!session) {
      this._session.set(null);
      this._user.set(null);
      return null;
    }

    const {
      data: { user },
      error: userError,
    } = await this.supabase.auth.getUser();
    if (userError || !user) {
      await this.supabase.auth.signOut();
      this._session.set(null);
      this._user.set(null);
      return null;
    }

    this._session.set(session);
    this._user.set(user);
    return session;
  }

  async waitForSession(): Promise<Session | null> {
    if (this._loading()) {
      const maxWait = 5000;
      const startTime = Date.now();
      while (this._loading() && Date.now() - startTime < maxWait) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    if (this._session() !== null) {
      return this._session();
    }

    try {
      const {
        data: { session },
        error,
      } = await this.supabase.auth.getSession();
      if (error) {
        this._error.set(error);
        return null;
      }
      return await this.resolveValidatedSession(session);
    } catch (error) {
      this._error.set(error as AuthError);
      return null;
    }
  }
}
